import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  Eye,
  FilePdf,
  FloppyDisk,
  Buildings,
  X,
  MagnifyingGlass,
  Spinner,
  Globe,
  BookmarkSimple,
  ListPlus,
  CaretUp,
  CaretDown,
  PencilSimple,
  ImageSquare,
  Star,
  Link,
  MapTrifold,
  ArrowLeft,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import ProposalPreview from "@/components/ProposalPreview";
import GridProposalEditor from "@/components/GridProposalEditor";
// PDF is now generated server-side via /api/proposals/{id}/pdf

const CURRENCIES = [
  "USD", "EUR", "GBP", "INR", "AED", "AUD", "SGD", "THB", "MYR",
  "JPY", "CHF", "CAD", "NZD", "HKD", "MOP", "IDR", "LKR",
  "QAR", "SAR", "MVR", "FJD", "SCR", "MUR", "ZAR", "KES",
  "BHD", "OMR", "KWD", "JOD", "EGP", "TRY", "CNY", "KRW",
  "TWD", "VND", "PHP", "BRL", "NOK", "SEK", "DKK", "CZK",
];

const DEFAULT_HOTEL_INCLUSIONS = [
  "Daily complimentary breakfast",
  "USD 100 hotel credit",
  "Upgrade to next category, subject to availability",
  "Early check-in and Late check-out, subject to availability",
  "VIP Tagging and Amenities",
  "All meals (full board)",
  "Airport transfers",
  "Seaplane / boat transfers",
  "Honeymoon / anniversary amenities",
  "Dedicated concierge support",
  "Complimentary Wi-Fi",
  "Minibar included",
  "Spa credit / treatment",
  "Club lounge access",
];

const DEFAULT_TERMS = [
  "Rates are indicative and subject to confirmation at time of booking.",
  "Quoted price excludes international airfare unless specified.",
  "Muskan Travel Associates acts as agent; hotel terms and conditions apply.",
  "Cancellation charges apply as per individual hotel policies.",
];

const HOTEL_INCLUSIONS = DEFAULT_HOTEL_INCLUSIONS.map((label, i) => ({
  id: `inc-${i}`,
  label,
}));

const TERM_CATEGORIES = ["General", "Cancellation", "Payment", "Booking", "Other"];
const INCLUSION_CATEGORIES = ["General", "Meals", "Transfers", "Amenities", "Experiences", "Other"];

const NewProposalPage = () => {
  const navigate = useNavigate();
  const { id: proposalId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const proposalType = searchParams.get("type") || "combined";
  const isEditing = Boolean(proposalId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | "saving" | "saved" | "error"
  const autoSaveTimer = useRef(null);
  const lastSavedData = useRef(null);
  const savedProposalId = useRef(proposalId || null);
  const [showPreview, setShowPreview] = useState(false);
  const [settings, setSettings] = useState(null);
  const [hotelLibrary, setHotelLibrary] = useState([]);
  const [tourLibrary, setTourLibrary] = useState([]);
  const [globalHotelSearch, setGlobalHotelSearch] = useState("");
  const [globalHotelResults, setGlobalHotelResults] = useState([]);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [searchingGlobal, setSearchingGlobal] = useState(false);
  const [clients, setClients] = useState([]);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({});
  const [quotationText, setQuotationText] = useState("");
  const [parsingQuotation, setParsingQuotation] = useState(false);
  const [showQuotationBox, setShowQuotationBox] = useState(false);
  
  // Libraries for terms and inclusions
  const [termsLibrary, setTermsLibrary] = useState([]);
  const [inclusionsLibrary, setInclusionsLibrary] = useState([]);
  
  // Dialogs for adding from library
  const [showTermsLibrary, setShowTermsLibrary] = useState(false);
  const [showInclusionsLibrary, setShowInclusionsLibrary] = useState(false);
  const [addingInclusionForHotel, setAddingInclusionForHotel] = useState(null);
  
  // New term/inclusion to save
  const [newTermToSave, setNewTermToSave] = useState({ text: "", category: "General" });
  const [newInclusionToSave, setNewInclusionToSave] = useState({ text: "", category: "General" });
  const [showSaveTermDialog, setShowSaveTermDialog] = useState(false);
  const [showSaveInclusionDialog, setShowSaveInclusionDialog] = useState(false);
  
  // Editing an existing inclusion in library
  const [editingInclusion, setEditingInclusion] = useState(null);
  
  // Hotel search state
  const [showHotelSearch, setShowHotelSearch] = useState(false);
  const [searchingHotel, setSearchingHotel] = useState(null);
  const [hotelSearchQuery, setHotelSearchQuery] = useState("");
  const [hotelSearchResults, setHotelSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Form state
  const [proposal, setProposal] = useState({
    reference: "",
    proposal_date: new Date().toISOString().split("T")[0],
    trip_title: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    proposal_mode: "detailed",
    hotels: [],
    itineraries: [],
    grid_data: { columns: ["Non-Refundable", "Refundable", "W/o Breakfast"], cities: [] },
    inclusions: [],
    custom_inclusions: "",
    guarantee_policy: "",
    cancellation_policy: "",
    terms: [...DEFAULT_TERMS],
    consultant_name: "",
    consultant_designation: "",
    consultant_phone: "",
    consultant_email: "",
    status: "Draft",
    show_summary: true,
    show_inr: true,
  });

  // Load settings, hotels library, clients
  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, hotelsRes, clientsRes, termsRes, inclusionsRes, toursRes] = await Promise.all([
          api.get("/settings"),
          api.get("/hotels"),
          api.get("/clients"),
          api.get("/terms"),
          api.get("/inclusions"),
          api.get("/tours"),
        ]);

        // Fetch exchange rates (non-blocking)
        api.get("/exchange-rates").then((res) => setExchangeRates(res.data)).catch(() => {});

        // Fetch brands for auto-suggest (non-blocking)
        api.get("/hotels/brands").then((res) => setProposal((prev) => ({ ...prev, _brands: res.data }))).catch(() => {});

        setSettings(settingsRes.data);
        setHotelLibrary(hotelsRes.data);
        setTourLibrary(toursRes.data);
        setClients(clientsRes.data);
        setTermsLibrary(termsRes.data);
        setInclusionsLibrary(inclusionsRes.data);

        // Load logo if available
        if (settingsRes.data.agency_logo_path) {
          try {
            const logoResponse = await api.get(`/files/${settingsRes.data.agency_logo_path}`, {
              responseType: 'blob'
            });
            const reader = new FileReader();
            reader.onload = () => setLogoDataUrl(reader.result);
            reader.readAsDataURL(logoResponse.data);
          } catch (logoError) {
            console.error("Failed to load logo:", logoError);
          }
        }

        // Set default consultant info from settings
        if (!isEditing) {
          const s = settingsRes.data;
          const defaultInc = s.default_inclusions?.length > 0 ? s.default_inclusions : undefined;
          
          // Get next reference
          try {
            const refRes = await api.get("/proposals/next-reference");
            setProposal((prev) => {
              const updates = {
                ...prev,
                reference: refRes.data.reference,
                consultant_name: s.consultant_name || "",
                consultant_designation: s.consultant_designation || "",
                consultant_phone: s.consultant_phone || "",
                consultant_email: s.consultant_email || "",
                terms: s.default_terms?.length > 0 ? s.default_terms : DEFAULT_TERMS,
              };
              if (proposalType !== "sightseeing") updates.hotels = [createEmptyHotel(defaultInc)];
              if (proposalType !== "hotels") updates.itineraries = [{ id: crypto.randomUUID(), title: "", city: "", currency: "USD", pricing_mode: "per_tour", package_price: 0, adults: 0, cost_type: "total", tours: [] }];
              return updates;
            });
          } catch (refError) {
            console.error("Failed to get next reference:", refError);
            setProposal((prev) => {
              const updates = {
                ...prev,
                reference: `MTA-${new Date().toISOString().split("T")[0].replace(/-/g, "")}-1`,
                consultant_name: s.consultant_name || "",
                consultant_designation: s.consultant_designation || "",
                consultant_phone: s.consultant_phone || "",
                consultant_email: s.consultant_email || "",
                terms: s.default_terms?.length > 0 ? s.default_terms : DEFAULT_TERMS,
              };
              if (proposalType !== "sightseeing") updates.hotels = [createEmptyHotel(defaultInc)];
              if (proposalType !== "hotels") updates.itineraries = [{ id: crypto.randomUUID(), title: "", city: "", currency: "USD", pricing_mode: "per_tour", package_price: 0, adults: 0, cost_type: "total", tours: [] }];
              return updates;
            });
          }
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        if (!isEditing) {
          setProposal((prev) => {
            const updates = { ...prev };
            if (proposalType !== "sightseeing" && prev.hotels.length === 0) {
              updates.hotels = [createEmptyHotel()];
            }
            if (proposalType !== "hotels" && (!prev.itineraries || prev.itineraries.length === 0)) {
              updates.itineraries = [{ id: crypto.randomUUID(), title: "", city: "", currency: "USD", pricing_mode: "per_tour", package_price: 0, adults: 0, cost_type: "total", tours: [] }];
            }
            return updates;
          });
        }
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Load existing proposal if editing
  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      api.get(`/proposals/${proposalId}`)
        .then((res) => {
          const p = res.data;
          // Update proposal date to today when editing
          p.proposal_date = new Date().toISOString().split("T")[0];
          // Backward compatibility: if hotels don't have dates_per_room flag,
          // check if rooms have individual dates set — if so, default to dates_per_room: true
          if (p.hotels) {
            p.hotels = p.hotels.map((h) => {
              if (h.dates_per_room === undefined || h.dates_per_room === null) {
                const hasRoomDates = h.rooms?.some((r) => r.check_in || r.check_out);
                const hasHotelDates = h.check_in || h.check_out;
                return { ...h, dates_per_room: hasRoomDates && !hasHotelDates ? true : false };
              }
              return h;
            });
          }
          savedProposalId.current = proposalId;
          setProposal(p);
        })
        .catch(() => {
          toast.error("Failed to load proposal");
          navigate("/proposals");
        })
        .finally(() => setLoading(false));
    }
  }, [proposalId, isEditing, navigate]);

  // Apply imported PDF data if navigated from Import
  useEffect(() => {
    const imported = location.state?.importedData;
    if (!imported || isEditing || loading) return;
    const buildHotels = (imported.hotels || []).map((h) => ({
      ...createEmptyHotel(),
      ...h,
      id: crypto.randomUUID(),
      rooms: (h.rooms || []).map((r) => ({ ...createEmptyRoom(), ...r, id: crypto.randomUUID() })),
    }));
    const buildItins = (imported.itineraries || []).map((it) => ({
      id: crypto.randomUUID(), title: it.title || "", city: it.city || "", currency: it.currency || "USD",
      adults: it.adults || 0, cost_type: it.cost_type || "total", pricing_mode: it.pricing_mode || "per_tour",
      package_price: it.package_price || 0, cancellation_policy: it.cancellation_policy || "",
      tours: (it.tours || []).map((t) => ({ id: crypto.randomUUID(), ...t })),
    }));
    setProposal((prev) => ({
      ...prev,
      reference: imported.reference || prev.reference,
      proposal_date: imported.proposal_date || prev.proposal_date,
      trip_title: imported.trip_title || "",
      client_name: imported.client_name || "",
      consultant_name: imported.consultant_name || prev.consultant_name,
      consultant_designation: imported.consultant_designation || prev.consultant_designation,
      consultant_phone: imported.consultant_phone || prev.consultant_phone,
      consultant_email: imported.consultant_email || prev.consultant_email,
      hotels: buildHotels.length ? buildHotels : prev.hotels,
      itineraries: buildItins.length ? buildItins : prev.itineraries,
      terms: imported.terms?.length ? imported.terms : prev.terms,
    }));
    toast.success("PDF data loaded into editor — review and save");
    // Clear the state to prevent re-apply on refresh
    window.history.replaceState({}, document.title);
  }, [loading, isEditing, location.state]);

  // Create empty hotel
  const createEmptyHotel = (defaultInclusions) => ({
    id: crypto.randomUUID(),
    hotel_name: "",
    brand: "",
    city: "",
    country: "",
    city_group: "",
    address: "",
    description: "",
    amenities: "",
    currency: "USD",
    image_url: "",
    images: [],
    images_separate_page: false,
    inclusions: defaultInclusions || ["Daily breakfast", "Dedicated concierge support"],
    cancellation_policy: "",
    static_rate: false,
    dates_per_room: false,
    check_in: "",
    check_out: "",
    nights: 0,
    rooms: [createEmptyRoom()],
  });

  // Create empty room
  const createEmptyRoom = () => ({
    id: crypto.randomUUID(),
    room_type: "",
    adults: 2,
    children: 0,
    check_in: "",
    check_out: "",
    nights: 0,
    num_rooms: 1,
    room_charge: 0,
    service_charge: 0,
    tax: 0,
    total: 0,
    meal_plan: "",
    cost_price: 0,
  });

  // Add hotel block
  const addHotel = useCallback(() => {
    const defaultInc = settings?.default_inclusions?.length > 0
      ? settings.default_inclusions
      : undefined;
    setProposal((prev) => ({ ...prev, hotels: [...prev.hotels, createEmptyHotel(defaultInc)] }));
  }, [settings]);

  // Remove hotel
  const removeHotel = (hotelId) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.filter((h) => h.id !== hotelId),
    }));
  };

  // Global hotel search
  const searchGlobalHotels = async (q) => {
    setGlobalHotelSearch(q);
    if (q.length < 2) { setGlobalHotelResults([]); return; }
    setSearchingGlobal(true);
    try {
      const res = await api.get(`/global-hotels?q=${encodeURIComponent(q)}`);
      setGlobalHotelResults(res.data);
    } catch { setGlobalHotelResults([]); }
    setSearchingGlobal(false);
  };

  const addFromGlobalHotel = (gh) => {
    const newHotel = {
      ...createEmptyHotel(),
      hotel_name: gh.hotel_name,
      city: gh.city,
      country: gh.country,
      address: gh.address || "",
      description: gh.description || "",
      currency: gh.currency || "USD",
      rooms: (gh.rooms || []).map((r) => ({
        ...createEmptyRoom(),
        room_type: r.room_type || "",
        room_charge: r.rate || 0,
      })),
    };
    if (newHotel.rooms.length === 0) newHotel.rooms = [createEmptyRoom()];
    setProposal((p) => ({ ...p, hotels: [...p.hotels, newHotel] }));
    setShowGlobalSearch(false);
    setGlobalHotelSearch("");
    setGlobalHotelResults([]);
    toast.success(`${gh.hotel_name} added with ${gh.rooms?.length || 0} room types`);
  };

  // Itinerary helpers
  const addItinerary = () => {
    setProposal((p) => ({
      ...p,
      itineraries: [...(p.itineraries || []), {
        id: crypto.randomUUID(), title: "", city: "", currency: "USD",
        pricing_mode: "per_tour", package_price: 0, adults: 0, cost_type: "total", tours: [],
      }],
    }));
  };

  const updateItinerary = (idx, field, value) => {
    setProposal((p) => ({
      ...p,
      itineraries: p.itineraries.map((it, i) => i === idx ? { ...it, [field]: value } : it),
    }));
  };

  const addTour = (itinIdx) => {
    setProposal((p) => ({
      ...p,
      itineraries: p.itineraries.map((it, i) =>
        i === itinIdx ? { ...it, tours: [...it.tours, { id: crypto.randomUUID(), name: "", description: "", day_date: "", start_time: "", end_time: "", duration: "", cost: 0, currency: it.currency, pickup: "", notes: "", inclusions: [], map_link: "", tour_type: "activity" }] } : it
      ),
    }));
  };

  const addTourFromLibrary = (itinIdx, libId) => {
    const libTour = tourLibrary.find((t) => t.id === libId);
    if (!libTour) return;
    setProposal((p) => ({
      ...p,
      itineraries: p.itineraries.map((it, i) =>
        i === itinIdx ? { ...it, tours: [...it.tours, {
          id: crypto.randomUUID(), name: libTour.name, description: libTour.description,
          day_date: "", start_time: "", end_time: "", duration: libTour.duration, cost: libTour.default_cost,
          currency: libTour.default_currency, pickup: libTour.pickup,
          notes: "", inclusions: [], map_link: "", tour_type: "activity",
        }] } : it
      ),
    }));
  };

  const updateTour = (itinIdx, tourIdx, field, value) => {
    setProposal((p) => ({
      ...p,
      itineraries: p.itineraries.map((it, i) =>
        i === itinIdx ? { ...it, tours: it.tours.map((t, j) => j === tourIdx ? { ...t, [field]: value } : t) } : it
      ),
    }));
  };

  const removeTour = (itinIdx, tourId) => {
    setProposal((p) => ({
      ...p,
      itineraries: p.itineraries.map((it, i) =>
        i === itinIdx ? { ...it, tours: it.tours.filter((t) => t.id !== tourId) } : it
      ),
    }));
  };

  const moveHotel = (index, direction) => {
    setProposal((prev) => {
      const hotels = [...prev.hotels];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= hotels.length) return prev;
      [hotels[index], hotels[newIndex]] = [hotels[newIndex], hotels[index]];
      return { ...prev, hotels };
    });
  };


  // Update hotel field
  const updateHotel = (hotelId, field, value) => {
    setProposal((prev) => {
      let updatedHotels = prev.hotels.map((h) => {
        if (h.id !== hotelId) return h;
        const updated = { ...h, [field]: value };

        // Auto-calculate hotel-level nights (timezone-safe)
        if (field === "check_in" || field === "check_out") {
          if (updated.check_in && updated.check_out) {
            const [y1, m1, d1] = updated.check_in.split("-").map(Number);
            const [y2, m2, d2] = updated.check_out.split("-").map(Number);
            const cin = new Date(y1, m1 - 1, d1);
            const cout = new Date(y2, m2 - 1, d2);
            const nights = Math.round((cout - cin) / 86400000);
            if (nights > 0) updated.nights = nights;
            else if (nights <= 0) updated.nights = 0;
          }
        }

        // Propagate hotel-level dates to all rooms when in "dates per hotel" mode
        if (!updated.dates_per_room && (field === "check_in" || field === "check_out" || field === "nights")) {
          updated.rooms = updated.rooms.map((r) => ({
            ...r,
            check_in: updated.check_in,
            check_out: updated.check_out,
            nights: updated.nights,
          }));
        }

        // When switching from "dates per room" to "dates per hotel", copy hotel dates to all rooms
        if (field === "dates_per_room" && !value && updated.check_in) {
          updated.rooms = updated.rooms.map((r) => ({
            ...r,
            check_in: updated.check_in,
            check_out: updated.check_out,
            nights: updated.nights,
          }));
        }

        return updated;
      });

      // Propagate dates to other hotels in the same city_group
      if ((field === "check_in" || field === "check_out" || field === "nights") && updatedHotels.length > 1) {
        const src = updatedHotels.find((h) => h.id === hotelId);
        if (src?.city_group) {
          updatedHotels = updatedHotels.map((h) => {
            if (h.id === hotelId || h.city_group !== src.city_group) return h;
            const synced = { ...h, check_in: src.check_in, check_out: src.check_out, nights: src.nights };
            if (!synced.dates_per_room) {
              synced.rooms = synced.rooms.map((r) => ({ ...r, check_in: src.check_in, check_out: src.check_out, nights: src.nights }));
            }
            return synced;
          });
        }
      }

      return { ...prev, hotels: updatedHotels };
    });
  };

  // Toggle hotel inclusion
  const toggleHotelInclusion = (hotelId, inclusion) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) => {
        if (h.id !== hotelId) return h;
        const current = h.inclusions || [];
        const updated = current.includes(inclusion)
          ? current.filter((i) => i !== inclusion)
          : [...current, inclusion];
        return { ...h, inclusions: updated };
      }),
    }));
  };

  // Add room to hotel
  const addRoom = (hotelId) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) => {
        if (h.id !== hotelId) return h;
        const newRoom = {
          ...createEmptyRoom(),
          check_in: h.check_in || "",
          check_out: h.check_out || "",
          nights: h.nights || 0,
        };
        return { ...h, rooms: [...h.rooms, newRoom] };
      }),
    }));
  };

  // Remove room
  const removeRoom = (hotelId, roomId) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) =>
        h.id === hotelId
          ? { ...h, rooms: h.rooms.filter((r) => r.id !== roomId) }
          : h
      ),
    }));
  };

  // Sort rooms by price (low to high)
  const sortRoomsByPrice = (hotelId, direction = "asc") => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) => {
        if (h.id !== hotelId) return h;
        const sorted = [...h.rooms].sort((a, b) => {
          const aTotal = parseFloat(a.total || 0);
          const bTotal = parseFloat(b.total || 0);
          return direction === "asc" ? aTotal - bTotal : bTotal - aTotal;
        });
        return { ...h, rooms: sorted };
      }),
    }));
  };

  // Update room field
  const updateRoom = (hotelId, roomId, field, value) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) =>
        h.id === hotelId
          ? {
              ...h,
              rooms: h.rooms.map((r) => {
                if (r.id !== roomId) return r;
                const updated = { ...r, [field]: value };

                // Auto-calculate nights (timezone-safe)
                if (field === "check_in" || field === "check_out") {
                  if (updated.check_in && updated.check_out) {
                    const [y1, m1, d1] = updated.check_in.split("-").map(Number);
                    const [y2, m2, d2] = updated.check_out.split("-").map(Number);
                    const cin = new Date(y1, m1 - 1, d1);
                    const cout = new Date(y2, m2 - 1, d2);
                    const nights = Math.round((cout - cin) / 86400000);
                    if (nights > 0) updated.nights = nights;
                    else if (nights <= 0) updated.nights = 0;
                  }
                }

                // Auto-calculate total
                if (["room_charge", "service_charge", "tax"].includes(field)) {
                  updated.total =
                    parseFloat(updated.room_charge || 0) +
                    parseFloat(updated.service_charge || 0) +
                    parseFloat(updated.tax || 0);
                }

                return updated;
              }),
            }
          : h
      ),
    }));
  };

  // Hotel Search Functions
  const openHotelSearch = (hotelId) => {
    setSearchingHotel(hotelId);
    setHotelSearchQuery("");
    setHotelSearchResults([]);
    setShowHotelSearch(true);
  };

  const searchHotels = async () => {
    if (!hotelSearchQuery.trim()) {
      toast.error("Please enter a city or location");
      return;
    }
    
    setIsSearching(true);
    try {
      const res = await api.get(`/hotels/search?query=${encodeURIComponent(hotelSearchQuery)}`);
      setHotelSearchResults(res.data);
      if (res.data.length === 0) {
        toast.info("No hotels found. Try a different city or add manually.");
      }
    } catch (error) {
      toast.error("Search failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchResult = (result) => {
    if (searchingHotel) {
      setProposal((prev) => ({
        ...prev,
        hotels: prev.hotels.map((h) =>
          h.id === searchingHotel
            ? {
                ...h,
                hotel_name: result.name,
                city: result.city,
                country: result.country,
                address: result.address,
                description: result.description,
                image_url: result.image_url,
              }
            : h
        ),
      }));
      setShowHotelSearch(false);
      toast.success(`Selected: ${result.name}`);
    }
  };

  // Select hotel from library
  const selectHotelFromLibrary = (hotelId, libraryHotelId) => {
    const libHotel = hotelLibrary.find((h) => h.id === libraryHotelId);
    if (libHotel) {
      setProposal((prev) => ({
        ...prev,
        hotels: prev.hotels.map((h) =>
          h.id === hotelId
            ? {
                ...h,
                hotel_name: libHotel.hotel_name,
                city: libHotel.city,
                country: libHotel.country,
                address: libHotel.address,
                description: libHotel.description,
                amenities: libHotel.amenities,
                currency: libHotel.default_currency,
                image_url: libHotel.image_url || "",
                inclusions: libHotel.default_inclusions?.length > 0
                  ? libHotel.default_inclusions
                  : h.inclusions,
              }
            : h
        ),
      }));
    }
  };

  // Save hotel to library
  const saveToLibrary = async (hotel) => {
    if (!hotel.hotel_name) {
      toast.error("Please enter a hotel name first");
      return;
    }
    // Check if already saved
    const exists = hotelLibrary.some(
      (h) => h.hotel_name.toLowerCase().trim() === hotel.hotel_name.toLowerCase().trim()
    );
    if (exists) {
      toast.error(`"${hotel.hotel_name}" is already saved in your hotel library`);
      return;
    }
    try {
      await api.post("/hotels", {
        hotel_name: hotel.hotel_name,
        city: hotel.city,
        country: hotel.country,
        address: hotel.address,
        description: hotel.description,
        amenities: hotel.amenities,
        default_currency: hotel.currency,
        image_url: hotel.image_url,
        default_inclusions: hotel.inclusions || [],
      });
      toast.success("Hotel saved to library (with inclusions)");
      const res = await api.get("/hotels");
      setHotelLibrary(res.data);
    } catch (error) {
      toast.error("Failed to save hotel");
    }
  };

  // Add term
  const addTerm = () => {
    setProposal((prev) => ({ ...prev, terms: [...prev.terms, ""] }));
  };

  // Update term
  const updateTerm = (index, value) => {
    setProposal((prev) => ({
      ...prev,
      terms: prev.terms.map((t, i) => (i === index ? value : t)),
    }));
  };

  // Remove term
  const removeTerm = (index) => {
    setProposal((prev) => ({
      ...prev,
      terms: prev.terms.filter((_, i) => i !== index),
    }));
  };

  // Save term to library
  const saveTermToLibrary = async () => {
    if (!newTermToSave.text.trim()) {
      toast.error("Please enter term text");
      return;
    }
    try {
      await api.post("/terms", newTermToSave);
      toast.success("Term saved to library");
      const res = await api.get("/terms");
      setTermsLibrary(res.data);
      setShowSaveTermDialog(false);
      setNewTermToSave({ text: "", category: "General" });
    } catch (error) {
      toast.error("Failed to save term");
    }
  };

  // Add term from library
  const addTermFromLibrary = (termText) => {
    if (!proposal.terms.includes(termText)) {
      setProposal((prev) => ({ ...prev, terms: [...prev.terms, termText] }));
      toast.success("Term added");
    } else {
      toast.info("Term already in proposal");
    }
  };

  // Delete term from library
  const deleteTermFromLibrary = async (termId) => {
    try {
      await api.delete(`/terms/${termId}`);
      const res = await api.get("/terms");
      setTermsLibrary(res.data);
      toast.success("Term deleted from library");
    } catch (error) {
      toast.error("Failed to delete term");
    }
  };

  // Save inclusion to library
  const saveInclusionToLibrary = async () => {
    if (!newInclusionToSave.text.trim()) {
      toast.error("Please enter inclusion text");
      return;
    }
    try {
      await api.post("/inclusions", newInclusionToSave);
      toast.success("Inclusion saved to library");
      const res = await api.get("/inclusions");
      setInclusionsLibrary(res.data);
      setShowSaveInclusionDialog(false);
      setNewInclusionToSave({ text: "", category: "General" });
    } catch (error) {
      toast.error("Failed to save inclusion");
    }
  };

  // Add inclusion to hotel from library
  const addInclusionFromLibrary = (inclusionText) => {
    if (!addingInclusionForHotel) return;
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) => {
        if (h.id !== addingInclusionForHotel) return h;
        const current = h.inclusions || [];
        if (current.includes(inclusionText)) {
          toast.info("Inclusion already added");
          return h;
        }
        return { ...h, inclusions: [...current, inclusionText] };
      }),
    }));
    toast.success("Inclusion added");
  };

  // Delete inclusion from library
  const deleteInclusionFromLibrary = async (inclusionId) => {
    try {
      await api.delete(`/inclusions/${inclusionId}`);
      const res = await api.get("/inclusions");
      setInclusionsLibrary(res.data);
      toast.success("Inclusion deleted from library");
    } catch (error) {
      toast.error("Failed to delete inclusion");
    }
  };

  // Edit inclusion in library
  const updateInclusionInLibrary = async () => {
    if (!editingInclusion || !editingInclusion.text.trim()) {
      toast.error("Please enter inclusion text");
      return;
    }
    try {
      await api.put(`/inclusions/${editingInclusion.id}`, {
        text: editingInclusion.text,
        category: editingInclusion.category,
      });
      const res = await api.get("/inclusions");
      setInclusionsLibrary(res.data);
      setEditingInclusion(null);
      toast.success("Inclusion updated");
    } catch (error) {
      toast.error("Failed to update inclusion");
    }
  };

  // Add custom inclusion to hotel
  const addCustomInclusionToHotel = (hotelId, text) => {
    if (!text.trim()) return;
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) => {
        if (h.id !== hotelId) return h;
        const current = h.inclusions || [];
        if (current.includes(text)) return h;
        return { ...h, inclusions: [...current, text] };
      }),
    }));
  };

  // Remove inclusion from hotel
  const removeInclusionFromHotel = (hotelId, inclusionText) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h) => {
        if (h.id !== hotelId) return h;
        return { ...h, inclusions: (h.inclusions || []).filter(i => i !== inclusionText) };
      }),
    }));
  };

  // Open inclusions library for a hotel
  const openInclusionsLibrary = (hotelId) => {
    setAddingInclusionForHotel(hotelId);
    setShowInclusionsLibrary(true);
  };

  // Save proposal
  const saveProposal = async (status = proposal.status) => {
    if (!proposal.client_name) {
      toast.error("Please enter client name");
      return;
    }

    setSaving(true);
    try {
      const data = { ...proposal, status };
      if (isEditing) {
        await api.put(`/proposals/${proposalId}`, data);
        toast.success("Proposal updated");
      } else {
        const res = await api.post("/proposals", data);
        toast.success("Proposal saved");
        savedProposalId.current = res.data.id;
        navigate(`/proposals/${res.data.id}/edit`);
      }
      lastSavedData.current = JSON.stringify(proposal);
    } catch (error) {
      toast.error("Failed to save proposal");
    } finally {
      setSaving(false);
    }
  };

  // Auto-save: debounce 30 seconds after any change — only updates existing proposals
  useEffect(() => {
    // Only auto-save if we have a saved proposal ID (i.e., it's been saved at least once)
    const pid = savedProposalId.current;
    if (!pid || !proposal.client_name || loading) return;

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      const currentJson = JSON.stringify(proposal);
      if (currentJson === lastSavedData.current) return;

      // Recheck pid is still valid
      if (!savedProposalId.current) return;

      try {
        setAutoSaveStatus("saving");
        await api.put(`/proposals/${savedProposalId.current}`, { ...proposal });
        lastSavedData.current = currentJson;
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus(null), 3000);
      } catch {
        setAutoSaveStatus("error");
        setTimeout(() => setAutoSaveStatus(null), 3000);
      }
    }, 30000);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [proposal, loading]);

  // Set lastSavedData on initial load
  useEffect(() => {
    if (!loading && proposal.client_name) {
      lastSavedData.current = JSON.stringify(proposal);
    }
  }, [loading]);

  // Convert to INR with 4.13% card markup
  const toINR = useCallback((amount, currency) => {
    if (!amount || currency === "INR" || !exchangeRates[currency]) return null;
    return Math.round(parseFloat(amount) * exchangeRates[currency]);
  }, [exchangeRates]);

  const fmtINR = useCallback((amount) => {
    if (!amount) return "";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);
  }, []);

  // AI Quotation Parser
  const parseQuotation = async () => {
    if (!quotationText.trim()) {
      toast.error("Please paste a quotation first");
      return;
    }
    setParsingQuotation(true);
    try {
      const res = await api.post("/parse-quotation", { text: quotationText });
      const parsed = res.data;
      if (parsed.hotels && parsed.hotels.length > 0) {
        const newHotels = parsed.hotels.map((h) => ({
          id: crypto.randomUUID(),
          hotel_name: h.hotel_name || "",
          city: h.city || "",
          country: h.country || "",
          address: h.address || "",
          description: h.description || "",
          amenities: h.amenities || "",
          currency: h.currency || "USD",
          image_url: "",
          inclusions: h.inclusions || [],
          cancellation_policy: h.cancellation_policy || "",
          static_rate: false,
          dates_per_room: false,
          check_in: h.check_in || "",
          check_out: h.check_out || "",
          nights: h.nights || 0,
          rooms: (h.rooms || []).map((r) => ({
            id: crypto.randomUUID(),
            room_type: r.room_type || "",
            adults: r.adults || 2,
            children: r.children || 0,
            num_rooms: r.num_rooms || 1,
            check_in: h.check_in || "",
            check_out: h.check_out || "",
            nights: h.nights || 0,
            room_charge: r.room_charge || 0,
            service_charge: r.service_charge || 0,
            tax: r.tax || 0,
            total: r.total || 0,
          })),
        }));
        setProposal((prev) => ({
          ...prev,
          hotels: [...prev.hotels.filter((h) => h.hotel_name), ...newHotels],
        }));
        setQuotationText("");
        setShowQuotationBox(false);
        toast.success(`Parsed ${newHotels.length} hotel(s) from quotation`);
      } else {
        toast.error("Could not extract hotel details from the text");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse quotation");
    } finally {
      setParsingQuotation(false);
    }
  };

  // Download PDF — server-side generation for perfect output
  const downloadPDF = async () => {
    const isGrid = proposal.proposal_mode === "grid";
    const hasGridContent = isGrid && proposal.grid_data?.cities?.length > 0;
    const hasDetailedContent = !isGrid && (proposal.hotels.length > 0 || (proposal.itineraries?.length > 0));
    
    if (!proposal.client_name || (isGrid ? !hasGridContent : !hasDetailedContent)) {
      toast.error(isGrid
        ? "Please add client name and at least one city with hotels"
        : "Please add client name and at least one hotel or itinerary"
      );
      return;
    }

    try {
      setGeneratingPdf(true);

      // Save the proposal first
      const data = { ...proposal };
      let savedId = proposalId;

      if (proposalId) {
        await api.put(`/proposals/${proposalId}`, data);
      } else {
        const res = await api.post("/proposals", data);
        savedId = res.data.id;
        // Update URL so future saves work correctly
        window.history.replaceState(null, "", `/proposals/${savedId}/edit`);
      }

      if (!savedId) {
        toast.error("Failed to save proposal for PDF generation");
        return;
      }

      // Request server-generated PDF
      const response = await api.get(`/proposals/${savedId}/pdf`, {
        responseType: "blob",
      });

      // Check if response is actually an error (blob might contain JSON error)
      if (response.data.type === "application/json") {
        const text = await response.data.text();
        const err = JSON.parse(text);
        toast.error(err.detail || "PDF generation failed");
        return;
      }

      // Download the blob
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MTA_${proposal.reference || "Proposal"}_${(proposal.client_name || "Client").replace(/\s+/g, "_")}.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 500);

      toast.success("PDF downloaded");
    } catch (error) {
      console.error("PDF generation error:", error);
      const msg = error.response?.data?.detail || error.message || "Failed to generate PDF";
      toast.error(typeof msg === "string" ? msg : "Failed to generate PDF. Please try again.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertItems, setConvertItems] = useState([]);
  const [convertSelected, setConvertSelected] = useState([]);
  const [converting, setConverting] = useState(false);

  const openConvertDialog = async () => {
    const pid = savedProposalId.current;
    if (!pid) {
      toast.error("Please save the proposal first");
      return;
    }
    try {
      const res = await api.get(`/proposals/${pid}/hotels-for-conversion`);
      const items = res.data.items || [];
      if (items.length === 0) {
        toast.error("No hotel rooms found in this proposal to convert");
        return;
      }
      setConvertItems(items);
      setConvertSelected(items.map((_, i) => i)); // Select all by default
      setConvertDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load proposal data");
    }
  };

  const toggleConvertItem = (idx) => {
    setConvertSelected((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const executeConvert = async () => {
    if (convertSelected.length === 0) {
      toast.error("Select at least one hotel/room");
      return;
    }
    setConverting(true);
    try {
      const selectedItems = convertSelected.map((idx) => ({
        hotel_idx: convertItems[idx].hotel_idx,
        room_idx: convertItems[idx].room_idx,
      }));
      const res = await api.post(`/proposals/${savedProposalId.current}/convert-to-reservation`, {
        selected_items: selectedItems,
      });
      toast.success(`Reservation ${res.data.reservation_id} created!`);
      setConvertDialogOpen(false);
      navigate(`/reservations/${res.data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert");
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl" data-testid="proposal-builder">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(savedProposalId.current ? "/proposals" : "/dashboard")}
            className="text-muted-foreground hover:text-navy transition-colors mt-1"
            data-testid="back-btn"
            title="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">
            {isEditing ? "Edit Proposal" : `New ${proposalType === "hotels" ? "Hotel" : proposalType === "sightseeing" ? "Sightseeing" : "Combined"} Proposal`}
          </p>
          <h1 className="font-serif text-3xl text-navy">
            {proposal.trip_title || (proposalType === "sightseeing" ? "Sightseeing Proposal" : proposalType === "combined" ? "Travel Proposal" : "Hotel Proposal")}
          </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {autoSaveStatus && (
            <span className={`text-[11px] font-sans transition-opacity ${
              autoSaveStatus === "saved" ? "text-green-600" : autoSaveStatus === "saving" ? "text-muted-foreground" : "text-destructive"
            }`} data-testid="autosave-indicator">
              {autoSaveStatus === "saving" ? "Saving..." : autoSaveStatus === "saved" ? "Auto-saved" : "Save failed"}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => setShowPreview(true)}
            className="gap-2 border-border hover:border-navy"
            data-testid="preview-btn"
          >
            <Eye size={18} />
            Preview
          </Button>
          <Button
            onClick={downloadPDF}
            className="bg-gold hover:bg-gold/90 text-white gap-2"
            data-testid="download-pdf-btn"
            disabled={generatingPdf}
          >
            {generatingPdf ? <Spinner size={18} className="animate-spin" /> : <FilePdf size={18} />}
            {generatingPdf ? "Generating..." : "Download PDF"}
          </Button>
          {isEditing && (
            <Button
              variant="outline"
              onClick={openConvertDialog}
              className="gap-2 border-gold text-gold hover:bg-gold/10"
              data-testid="convert-to-reservation-btn"
            >
              <ArrowsClockwise size={18} />
              <span className="hidden sm:inline">Convert to</span> Reservation
            </Button>
          )}
        </div>
      </div>

      {/* Proposal Details Card */}
      <div className="bg-white border border-border p-6 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Proposal Details
        </h2>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reference</Label>
            <Input
              value={proposal.reference}
              onChange={(e) => setProposal((p) => ({ ...p, reference: e.target.value }))}
              className="bg-cream border-border"
              data-testid="proposal-reference"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
            <Input
              type="date"
              value={proposal.proposal_date}
              onChange={(e) => setProposal((p) => ({ ...p, proposal_date: e.target.value }))}
              className="bg-cream border-border"
              data-testid="proposal-date"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Trip Title</Label>
            <Input
              value={proposal.trip_title}
              onChange={(e) => setProposal((p) => ({ ...p, trip_title: e.target.value }))}
              placeholder="e.g. Japan Multi-City"
              className="bg-cream border-border"
              data-testid="trip-title"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
            <Select
              value={proposal.status}
              onValueChange={(v) => setProposal((p) => ({ ...p, status: v }))}
            >
              <SelectTrigger className="bg-cream border-border" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Sent">Sent</SelectItem>
                <SelectItem value="Confirmed">Confirmed</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client Name</Label>
            <Input
              value={proposal.client_name}
              onChange={(e) => setProposal((p) => ({ ...p, client_name: e.target.value }))}
              placeholder="Mr & Mrs Sharma"
              className="bg-cream border-border"
              list="client-suggestions"
              data-testid="client-name"
            />
            <datalist id="client-suggestions">
              {clients.map((c) => (
                <option key={c.id} value={c.name} />
              ))}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client Email</Label>
            <Input
              type="email"
              value={proposal.client_email}
              onChange={(e) => setProposal((p) => ({ ...p, client_email: e.target.value }))}
              placeholder="client@email.com"
              className="bg-cream border-border"
              data-testid="client-email"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client Phone</Label>
            <Input
              value={proposal.client_phone}
              onChange={(e) => setProposal((p) => ({ ...p, client_phone: e.target.value }))}
              placeholder="+91 XXXXX XXXXX"
              className="bg-cream border-border"
              data-testid="client-phone"
            />
          </div>
        </div>
      </div>

      {/* Hotels Section */}
      {(proposalType !== "sightseeing" || proposal.hotels?.length > 0) && (
      <div className="bg-white border border-border p-6 mb-4">
        <div className="flex items-center justify-between pb-4 border-b border-cream-mid mb-5">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
            Hotels & Rates
          </h2>
          <div className="flex items-center gap-3">
            {/* Proposal Mode Toggle */}
            <div className="flex items-center gap-2 bg-cream/80 border border-border px-2 py-1" data-testid="proposal-mode-toggle">
              <button
                onClick={() => setProposal((p) => ({ ...p, proposal_mode: "detailed" }))}
                className={`text-[10px] px-2.5 py-1 transition-colors ${
                  (proposal.proposal_mode || "detailed") === "detailed"
                    ? "bg-navy text-white font-medium"
                    : "text-muted-foreground hover:text-navy"
                }`}
                data-testid="mode-detailed"
              >
                Detailed
              </button>
              <button
                onClick={() => setProposal((p) => ({ ...p, proposal_mode: "grid" }))}
                className={`text-[10px] px-2.5 py-1 transition-colors ${
                  proposal.proposal_mode === "grid"
                    ? "bg-navy text-white font-medium"
                    : "text-muted-foreground hover:text-navy"
                }`}
                data-testid="mode-grid"
              >
                Grid / Compare
              </button>
            </div>
            {(proposal.proposal_mode || "detailed") === "detailed" && (
              <Button
                variant={showQuotationBox ? "default" : "outline"}
                size="sm"
                onClick={() => setShowQuotationBox((v) => !v)}
                className={`text-xs gap-1.5 ${showQuotationBox ? "bg-navy text-white" : "border-gold text-gold hover:bg-gold hover:text-white"}`}
                data-testid="ai-parse-toggle"
              >
                <MagnifyingGlass size={14} />
                {showQuotationBox ? "Close" : "Auto-fill from quotation"}
              </Button>
            )}
          </div>
        </div>

        {/* GRID MODE */}
        {proposal.proposal_mode === "grid" ? (
          <GridProposalEditor
            gridData={proposal.grid_data || { columns: ["Non-Refundable", "Refundable", "W/o Breakfast"], cities: [] }}
            onChange={(gd) => setProposal((p) => ({ ...p, grid_data: gd }))}
            inclusionsLibrary={inclusionsLibrary}
            defaultInclusions={DEFAULT_HOTEL_INCLUSIONS}
          />
        ) : (
        <>
        {/* DETAILED MODE (existing) */}

        {showQuotationBox && (
          <div className="bg-cream border border-gold/30 p-4 mb-5" data-testid="quotation-box">
            <p className="text-xs text-muted-foreground mb-2">
              Paste a hotel quotation (email, text, rates) and AI will extract hotel names, room types, dates, rates and fill the form automatically.
            </p>
            <textarea
              value={quotationText}
              onChange={(e) => setQuotationText(e.target.value)}
              placeholder={"e.g. Four Seasons Bangkok\nDeluxe Room - 2 adults\nCheck in: 10 May 2026, Check out: 14 May 2026\nRoom rate: THB 15,000/night\nService charge: 10%, Tax: 7%\nIncludes daily breakfast and airport transfers"}
              className="w-full border border-border bg-white p-3 text-sm min-h-[120px] resize-y focus:outline-none focus:border-navy mb-3"
              data-testid="quotation-textarea"
            />
            <div className="flex items-center gap-3">
              <Button
                onClick={parseQuotation}
                disabled={parsingQuotation || !quotationText.trim()}
                className="bg-navy text-white hover:bg-navy/90 gap-2 text-xs"
                data-testid="parse-quotation-btn"
              >
                {parsingQuotation ? <Spinner size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />}
                {parsingQuotation ? "Parsing..." : "Parse & Auto-fill"}
              </Button>
              <span className="text-[10px] text-muted-foreground">Powered by AI — review filled data after parsing</span>
            </div>
          </div>
        )}

        {proposal.hotels.map((hotel, hotelIndex) => (
          <HotelBlock
            key={hotel.id}
            hotel={hotel}
            hotelIndex={hotelIndex}
            proposal={proposal}
            totalHotels={proposal.hotels.length}
            hotelLibrary={hotelLibrary}
            onUpdate={(field, value) => updateHotel(hotel.id, field, value)}
            onRemove={() => removeHotel(hotel.id)}
            onGenerateConfirmation={() => {
              const params = new URLSearchParams({
                guest: proposal.client_name || "",
                hotel: hotel.hotel_name || "",
                address: hotel.address || "",
                city: hotel.city || "",
                checkin: hotel.check_in || "",
                checkout: hotel.check_out || "",
                nights: String(hotel.nights || 0),
                room: hotel.rooms?.[0]?.room_type || "",
                currency: hotel.currency || "USD",
              });
              navigate(`/confirmations/new?${params.toString()}`);
            }}
            onMoveUp={() => moveHotel(hotelIndex, -1)}
            onMoveDown={() => moveHotel(hotelIndex, 1)}
            onAddRoom={() => addRoom(hotel.id)}
            onRemoveRoom={(roomId) => removeRoom(hotel.id, roomId)}
            onUpdateRoom={(roomId, field, value) => updateRoom(hotel.id, roomId, field, value)}
            onSortRoomsByPrice={(dir) => sortRoomsByPrice(hotel.id, dir)}
            toINR={proposal.show_inr !== false ? toINR : () => null}
            fmtINR={fmtINR}
            onSelectFromLibrary={(libId) => selectHotelFromLibrary(hotel.id, libId)}
            onSaveToLibrary={() => saveToLibrary(hotel)}
            onSearchHotels={() => openHotelSearch(hotel.id)}
            onToggleInclusion={(inc) => toggleHotelInclusion(hotel.id, inc)}
            inclusionsLibrary={inclusionsLibrary}
            onOpenInclusionsLibrary={() => openInclusionsLibrary(hotel.id)}
            onAddCustomInclusion={(text) => addCustomInclusionToHotel(hotel.id, text)}
            onRemoveInclusion={(text) => removeInclusionFromHotel(hotel.id, text)}
            onSaveInclusionToLibrary={(text) => {
              setNewInclusionToSave({ text, category: "General" });
              setShowSaveInclusionDialog(true);
            }}
            canRemove={proposal.hotels.length > 1}
          />
        ))}

        <div className="flex gap-2 mt-4">
          <button
            onClick={addHotel}
            className="flex-1 border-2 border-dashed border-border hover:border-navy text-muted-foreground hover:text-navy py-3 text-sm transition-colors"
            data-testid="add-hotel-btn"
          >
            + Add hotel / city
          </button>
          <button
            onClick={() => setShowGlobalSearch(true)}
            className="flex-1 border-2 border-dashed border-gold hover:border-gold/80 text-gold hover:text-gold/80 py-3 text-sm transition-colors"
            data-testid="browse-hotels-btn"
          >
            Browse Hotel Database
          </button>
        </div>

        {/* Global Hotel Search Dialog */}
        <Dialog open={showGlobalSearch} onOpenChange={setShowGlobalSearch}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-navy">Search Hotel Database</DialogTitle>
            </DialogHeader>
            <Input
              value={globalHotelSearch}
              onChange={(e) => searchGlobalHotels(e.target.value)}
              placeholder="Search by hotel name, city, or country..."
              className="bg-white border-border"
              autoFocus
              data-testid="global-hotel-search"
            />
            <div className="max-h-[50vh] overflow-y-auto space-y-1">
              {searchingGlobal && <p className="text-xs text-muted-foreground text-center py-4">Searching...</p>}
              {!searchingGlobal && globalHotelSearch.length >= 2 && globalHotelResults.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No hotels found</p>
              )}
              {globalHotelResults.map((gh) => (
                <button
                  key={gh.id}
                  onClick={() => addFromGlobalHotel(gh)}
                  className="w-full text-left p-3 hover:bg-cream border border-transparent hover:border-border transition-colors"
                  data-testid={`global-hotel-${gh.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-sm font-bold text-navy">{gh.hotel_name}</span>
                    <span className="text-[10px] text-gold font-semibold">{gh.currency}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{gh.city}, {gh.country}</div>
                  {gh.rooms?.length > 0 && (
                    <div className="text-[10px] text-muted-foreground/60 mt-0.5">
                      {gh.rooms.length} room types: {gh.rooms.map((r) => r.room_type).join(", ")}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        </>
        )}
        {/* End mode-specific content */}

        {proposal.hotels.length > 1 && (
          <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border" data-testid="summary-toggle">
            <Checkbox
              id="show-summary"
              checked={proposal.show_summary !== false}
              onCheckedChange={(checked) => setProposal((p) => ({ ...p, show_summary: checked }))}
              className="border-border data-[state=checked]:bg-navy data-[state=checked]:border-navy"
            />
            <label htmlFor="show-summary" className="text-xs text-muted-foreground cursor-pointer">
              Include Proposal Summary table in PDF
            </label>
          </div>
        )}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border" data-testid="inr-toggle">
          <Checkbox
            id="show-inr"
            checked={proposal.show_inr !== false}
            onCheckedChange={(checked) => setProposal((p) => ({ ...p, show_inr: checked }))}
            className="border-border data-[state=checked]:bg-navy data-[state=checked]:border-navy"
          />
          <label htmlFor="show-inr" className="text-xs text-muted-foreground cursor-pointer">
            Show approx. INR conversion (incl. 4.13% card charges)
          </label>
        </div>
      </div>
      )}

      {/* Itinerary & Sightseeing */}
      {(proposalType !== "hotels" || proposal.itineraries?.length > 0) && (
      <div className="bg-white border border-border p-6 mb-4">
        <div className="flex items-center justify-between pb-4 border-b border-cream-mid mb-5">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold flex items-center gap-2">
            <MapTrifold size={14} /> Itinerary & Sightseeing
          </h2>
        </div>

        {proposal.itineraries?.map((itin, iIdx) => (
          <div key={itin.id} className="bg-cream border border-border mb-3 p-4" data-testid={`itinerary-block-${iIdx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] bg-gold text-white px-3 py-1">Itinerary {iIdx + 1}</span>
              </div>
              <div className="flex gap-2">
                {proposal.itineraries.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setProposal((p) => ({ ...p, itineraries: p.itineraries.filter((it) => it.id !== itin.id) }))} className="text-xs h-7 text-muted-foreground hover:text-destructive">Remove</Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Section Title</Label>
                <Input value={itin.title} onChange={(e) => updateItinerary(iIdx, "title", e.target.value)} placeholder="e.g. Bali Sightseeing" className="bg-white border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">City</Label>
                <Input value={itin.city} onChange={(e) => updateItinerary(iIdx, "city", e.target.value)} placeholder="e.g. Bali" className="bg-white border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">No. of Guests</Label>
                <Input type="number" value={itin.adults || ""} onChange={(e) => updateItinerary(iIdx, "adults", parseInt(e.target.value) || 0)} placeholder="e.g. 4" className="bg-white border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Pricing Mode</Label>
                <Select value={itin.pricing_mode} onValueChange={(v) => updateItinerary(iIdx, "pricing_mode", v)}>
                  <SelectTrigger className="bg-white border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_tour">Per-Tour Pricing</SelectItem>
                    <SelectItem value="package">Package Price</SelectItem>
                    <SelectItem value="both">Both (Itemized + Total)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Cost Display</Label>
                <Select value={itin.cost_type || "total"} onValueChange={(v) => updateItinerary(iIdx, "cost_type", v)}>
                  <SelectTrigger className="bg-white border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total">Total for all guests</SelectItem>
                    <SelectItem value="per_person">Per person</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(itin.pricing_mode === "package" || itin.pricing_mode === "both") && (
              <>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Package Price</Label>
                <Input type="number" value={itin.package_price || ""} onChange={(e) => updateItinerary(iIdx, "package_price", parseFloat(e.target.value) || 0)} placeholder="0" className="bg-white border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Currency</Label>
                <Select value={itin.currency} onValueChange={(v) => updateItinerary(iIdx, "currency", v)}>
                  <SelectTrigger className="bg-white border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              </>
              )}
            </div>

            {/* Tour Items */}
            {itin.tours?.map((tour, tIdx) => (
              <div key={tour.id} className={`bg-white border-l-2 ${tour.tour_type === "transfer" ? "border-navy/40" : tour.tour_type === "meal" ? "border-emerald-400" : "border-gold"} p-3 mb-2`} data-testid={`tour-item-${iIdx}-${tIdx}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Select value={tour.tour_type || "activity"} onValueChange={(v) => updateTour(iIdx, tIdx, "tour_type", v)}>
                      <SelectTrigger className="h-5 w-[90px] text-[9px] bg-transparent border-none p-0 font-bold uppercase tracking-wider text-muted-foreground"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activity">Activity</SelectItem>
                        <SelectItem value="transfer">Transfer</SelectItem>
                        <SelectItem value="meal">Meal</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-[8px] text-muted-foreground/50">#{tIdx + 1}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeTour(iIdx, tour.id)} className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"><Trash size={12} /></Button>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <Input value={tour.name} onChange={(e) => updateTour(iIdx, tIdx, "name", e.target.value)} placeholder={tour.tour_type === "transfer" ? "e.g. Airport to Hotel" : tour.tour_type === "meal" ? "e.g. Dinner at Locavore" : "Activity name"} className="bg-white border-border text-sm h-8" />
                  <Input value={tour.day_date} onChange={(e) => updateTour(iIdx, tIdx, "day_date", e.target.value)} placeholder="Day 1 / Date" className="bg-white border-border text-sm h-8" />
                  <div className="flex gap-1">
                    <Input value={tour.start_time} onChange={(e) => updateTour(iIdx, tIdx, "start_time", e.target.value)} placeholder="Start" className="bg-white border-border text-xs h-8 w-1/2" />
                    <Input value={tour.end_time} onChange={(e) => updateTour(iIdx, tIdx, "end_time", e.target.value)} placeholder="End" className="bg-white border-border text-xs h-8 w-1/2" />
                  </div>
                </div>
                <Textarea value={tour.description} onChange={(e) => updateTour(iIdx, tIdx, "description", e.target.value)} placeholder="Description (optional)" className="bg-white border-border min-h-[36px] text-xs mb-2" />
                <div className="grid grid-cols-4 gap-2 mb-2">
                  <Input value={tour.duration} onChange={(e) => updateTour(iIdx, tIdx, "duration", e.target.value)} placeholder="Duration" className="bg-white border-border text-xs h-7" />
                  <Input type="number" value={tour.cost || ""} onChange={(e) => updateTour(iIdx, tIdx, "cost", parseFloat(e.target.value) || 0)} placeholder="Cost" className="bg-white border-border text-xs h-7" />
                  <Input value={tour.pickup} onChange={(e) => updateTour(iIdx, tIdx, "pickup", e.target.value)} placeholder="Pickup details" className="bg-white border-border text-xs h-7" />
                  <Select value={tour.currency || itin.currency} onValueChange={(v) => updateTour(iIdx, tIdx, "currency", v)}>
                    <SelectTrigger className="bg-white border-border text-xs h-7"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-1">
                  <Input value={tour.notes || ""} onChange={(e) => updateTour(iIdx, tIdx, "notes", e.target.value)} placeholder="Notes / tips for client" className="bg-white border-border text-xs h-7" />
                  <Input value={tour.map_link || ""} onChange={(e) => updateTour(iIdx, tIdx, "map_link", e.target.value)} placeholder="Google Maps link" className="bg-white border-border text-xs h-7" />
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {["Entrance fees", "Lunch", "Guide", "Transport", "Equipment"].map((inc) => (
                    <label key={inc} className="flex items-center gap-1 cursor-pointer">
                      <Checkbox
                        checked={(tour.inclusions || []).includes(inc)}
                        onCheckedChange={(checked) => {
                          const cur = tour.inclusions || [];
                          updateTour(iIdx, tIdx, "inclusions", checked ? [...cur, inc] : cur.filter((i) => i !== inc));
                        }}
                        className="h-3 w-3 border-border data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                      />
                      <span className="text-[10px] text-muted-foreground">{inc}</span>
                    </label>
                  ))}
                </div>
                {proposal.show_inr !== false && tour.cost > 0 && toINR(tour.cost, tour.currency || itin.currency) && (
                  <p className="text-[10px] text-muted-foreground mt-1 text-right">Approx. {fmtINR(toINR(tour.cost, tour.currency || itin.currency))}</p>
                )}
              </div>
            ))}

            <div className="flex gap-2 mt-2">
              <button onClick={() => addTour(iIdx)} className="text-xs text-navy hover:text-gold border border-dashed border-border hover:border-gold px-3 py-1.5 transition-colors" data-testid={`add-tour-${iIdx}`}>+ Add tour / activity</button>
              {tourLibrary.length > 0 && (
                <Select onValueChange={(libId) => addTourFromLibrary(iIdx, libId)}>
                  <SelectTrigger className="w-[180px] h-8 text-xs bg-white border-border">
                    <MapTrifold size={12} className="mr-1" />
                    <SelectValue placeholder="From library" />
                  </SelectTrigger>
                  <SelectContent>
                    {tourLibrary.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Cancellation Policy */}
            <div className="mt-3 space-y-1">
              <Label className="text-xs text-muted-foreground">Cancellation Policy</Label>
              <Textarea value={itin.cancellation_policy || ""} onChange={(e) => updateItinerary(iIdx, "cancellation_policy", e.target.value)} placeholder="e.g. Free cancellation 48 hours before..." className="bg-white border-border min-h-[40px] text-xs" />
            </div>
          </div>
        ))}

        <button
          onClick={addItinerary}
          className="w-full border-2 border-dashed border-border hover:border-gold text-muted-foreground hover:text-gold py-3 text-sm transition-colors"
          data-testid="add-itinerary-btn"
        >
          + Add itinerary / sightseeing section
        </button>
      </div>
      )}

      {/* Policies & Terms */}
      <div className="bg-white border border-border p-6 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Policies & Terms
        </h2>
        <div className="space-y-2 mb-4">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Guarantee / Deposit Policy</Label>
          <Textarea
            value={proposal.guarantee_policy}
            onChange={(e) => setProposal((p) => ({ ...p, guarantee_policy: e.target.value }))}
            placeholder="Credit card required for guarantee only."
            className="bg-cream border-border"
            data-testid="guarantee-policy"
          />
          <p className="text-[10px] text-muted-foreground">Cancellation policies are set per hotel in the hotel blocks above.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Terms & Conditions</Label>
          {proposal.terms.map((term, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={term}
                onChange={(e) => updateTerm(i, e.target.value)}
                className="bg-cream border-border text-sm"
                data-testid={`term-${i}`}
              />
              <button
                onClick={() => removeTerm(i)}
                className="px-2 text-muted-foreground hover:text-destructive"
              >
                <X size={16} />
              </button>
              <button
                onClick={() => {
                  setNewTermToSave({ text: term, category: "General" });
                  setShowSaveTermDialog(true);
                }}
                className="px-2 text-muted-foreground hover:text-gold"
                title="Save to library"
              >
                <BookmarkSimple size={16} />
              </button>
            </div>
          ))}
          <div className="flex gap-2 mt-2">
            <button
              onClick={addTerm}
              className="border border-dashed border-border hover:border-navy text-muted-foreground hover:text-navy px-3 py-1.5 text-xs"
              data-testid="add-term-btn"
            >
              + Add term
            </button>
            <button
              onClick={() => setShowTermsLibrary(true)}
              className="border border-gold text-gold hover:bg-gold hover:text-white px-3 py-1.5 text-xs flex items-center gap-1"
            >
              <ListPlus size={14} /> From Library
            </button>
          </div>
        </div>
      </div>

      {/* Commission Tracker */}
      <div className="bg-white border border-border p-6 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Commission / Margin Tracker <span className="font-normal text-muted-foreground/60 normal-case tracking-normal ml-2">— internal only, never shown on PDF</span>
        </h2>
        {proposal.hotels.length > 0 && proposal.hotels.some(h => h.rooms.some(r => r.room_type)) ? (
          proposal.hotels.map((hotel) => (
            <div key={hotel.id} className="mb-6">
              <h3 className="text-sm font-semibold text-navy uppercase tracking-wide mb-3">
                {hotel.hotel_name || `Hotel ${proposal.hotels.indexOf(hotel) + 1}`}
                <span className="text-muted-foreground font-normal ml-2">({hotel.currency})</span>
              </h3>
              {hotel.rooms.filter(r => r.room_type).map((room) => {
                const margin = room.total - room.cost_price;
                const marginPct = room.total > 0 ? ((margin / room.total) * 100).toFixed(1) : 0;
                return (
                  <div key={room.id} className="grid grid-cols-4 gap-3 mb-3 p-3 bg-cream">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{room.room_type} — Cost Price ({hotel.currency})</Label>
                      <Input
                        type="number"
                        value={room.cost_price || ""}
                        onChange={(e) => updateRoom(hotel.id, room.id, "cost_price", parseFloat(e.target.value) || 0)}
                        placeholder="Your net rate"
                        className="bg-white border-border"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Selling Price ({hotel.currency})</Label>
                      <Input
                        value={room.total.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        readOnly
                        className="bg-cream-mid border-border"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Gross Margin</Label>
                      <Input
                        value={margin > 0 ? `${hotel.currency} ${margin.toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}
                        readOnly
                        className="bg-cream-mid border-border font-semibold text-navy"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Margin %</Label>
                      <Input
                        value={marginPct > 0 ? `${marginPct}%` : "—"}
                        readOnly
                        className="bg-cream-mid border-border font-semibold text-gold"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground py-2">Add hotels and rooms first — commission fields will appear here per hotel/room.</p>
        )}
      </div>

      {/* Consultant Details */}
      <div className="bg-white border border-border p-6 mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Consultant Details
        </h2>
        <div className="grid grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input
              value={proposal.consultant_name}
              onChange={(e) => setProposal((p) => ({ ...p, consultant_name: e.target.value }))}
              placeholder="Pratyush Sareen"
              className="bg-cream border-border"
              data-testid="consultant-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Designation</Label>
            <Input
              value={proposal.consultant_designation}
              onChange={(e) => setProposal((p) => ({ ...p, consultant_designation: e.target.value }))}
              placeholder="Director — Travel"
              className="bg-cream border-border"
              data-testid="consultant-designation"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone / WhatsApp</Label>
            <Input
              value={proposal.consultant_phone}
              onChange={(e) => setProposal((p) => ({ ...p, consultant_phone: e.target.value }))}
              placeholder="+91 XXXXX XXXXX"
              className="bg-cream border-border"
              data-testid="consultant-phone"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input
              value={proposal.consultant_email}
              onChange={(e) => setProposal((p) => ({ ...p, consultant_email: e.target.value }))}
              placeholder="pratyush@muskantravel.com"
              className="bg-cream border-border"
              data-testid="consultant-email"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-10">
        <Button
          variant="outline"
          onClick={() => setShowPreview(true)}
          className="gap-2 border-border hover:border-navy"
        >
          <Eye size={18} />
          <span className="hidden sm:inline">Preview proposal</span>
        </Button>
        <Button
          onClick={downloadPDF}
          className="bg-gold hover:bg-gold/90 text-white gap-2"
        >
          <FilePdf size={18} />
          <span className="hidden sm:inline">Download PDF</span>
        </Button>
        <Button
          onClick={() => saveProposal()}
          disabled={saving}
          className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
          data-testid="save-proposal-btn"
        >
          <FloppyDisk size={18} />
          <span className="hidden sm:inline">{saving ? "Saving..." : "Save to history"}</span>
        </Button>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto p-0">
          <DialogHeader className="bg-navy p-4 sticky top-0 z-10">
            <DialogTitle className="text-gold text-sm font-semibold tracking-wider">PROPOSAL PREVIEW</DialogTitle>
          </DialogHeader>
          <ProposalPreview proposal={proposal} settings={settings} logoDataUrl={logoDataUrl} exchangeRates={exchangeRates} />
        </DialogContent>
      </Dialog>

      {/* Hotel Search Modal */}
      <Dialog open={showHotelSearch} onOpenChange={setShowHotelSearch}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy flex items-center gap-2">
              <Globe size={24} className="text-gold" />
              Search Hotels Worldwide
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex gap-2 mb-4">
              <Input
                value={hotelSearchQuery}
                onChange={(e) => setHotelSearchQuery(e.target.value)}
                placeholder="Enter city name (e.g., Tokyo, Paris, Dubai)"
                className="bg-cream border-border"
                onKeyPress={(e) => e.key === "Enter" && searchHotels()}
              />
              <Button
                onClick={searchHotels}
                disabled={isSearching}
                className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
              >
                {isSearching ? <Spinner size={18} className="animate-spin" /> : <MagnifyingGlass size={18} />}
                Search
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mb-4">
              Powered by OpenStreetMap · Works best for major cities
            </p>

            <div className="max-h-[400px] overflow-y-auto">
              {hotelSearchResults.length > 0 ? (
                <div className="space-y-2">
                  {hotelSearchResults.map((result, idx) => (
                    <div
                      key={result.xid || idx}
                      className="flex gap-4 p-3 bg-cream hover:bg-cream-mid cursor-pointer transition-colors border border-transparent hover:border-gold"
                      onClick={() => selectSearchResult(result)}
                    >
                      {result.image_url ? (
                        <img src={result.image_url} alt={result.name} className="w-16 h-16 object-cover flex-shrink-0" onError={(e) => e.target.style.display = 'none'} />
                      ) : (
                        <div className="w-16 h-16 bg-cream-mid flex items-center justify-center flex-shrink-0">
                          <Buildings size={20} className="text-muted-foreground/50" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-navy line-clamp-1">{result.name}</h4>
                        <p className="text-xs text-muted-foreground">{result.city}{result.country ? `, ${result.country}` : ""}</p>
                        {result.address && <p className="text-xs text-muted-foreground line-clamp-1">{result.address}</p>}
                      </div>
                      <Button size="sm" variant="outline" className="self-center text-xs">Select</Button>
                    </div>
                  ))}
                </div>
              ) : isSearching ? (
                <div className="text-center py-8">
                  <Spinner size={32} className="mx-auto animate-spin text-navy mb-2" />
                  <p className="text-sm text-muted-foreground">Searching hotels...</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <MagnifyingGlass size={32} className="mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">Enter a city name to search</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Terms Library Dialog */}
      <Dialog open={showTermsLibrary} onOpenChange={setShowTermsLibrary}>
        <DialogContent className="max-w-lg max-h-[80vh]" data-testid="terms-library-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy">Terms & Conditions Library</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-muted-foreground mb-3">Click a term to add it to the current proposal.</p>
            {TERM_CATEGORIES.map((cat) => {
              const items = termsLibrary.filter((t) => t.category === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="mb-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gold mb-2">{cat}</h4>
                  <div className="space-y-1.5">
                    {items.map((t) => (
                      <div key={t.id} className="flex items-start gap-2 p-2 bg-cream hover:bg-cream-mid cursor-pointer border border-transparent hover:border-gold transition-colors" onClick={() => addTermFromLibrary(t.text)}>
                        <span className="text-sm flex-1">{t.text}</span>
                        <button onClick={(e) => { e.stopPropagation(); deleteTermFromLibrary(t.id); }} className="text-muted-foreground hover:text-destructive shrink-0"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {termsLibrary.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">No saved terms yet. Save terms from proposals to build your library.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTermsLibrary(false)} className="text-xs">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Term Dialog */}
      <Dialog open={showSaveTermDialog} onOpenChange={setShowSaveTermDialog}>
        <DialogContent className="max-w-md" data-testid="save-term-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg text-navy">Save Term to Library</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Term Text</Label>
              <Textarea value={newTermToSave.text} onChange={(e) => setNewTermToSave((p) => ({ ...p, text: e.target.value }))} className="bg-cream border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
              <Select value={newTermToSave.category} onValueChange={(v) => setNewTermToSave((p) => ({ ...p, category: v }))}>
                <SelectTrigger className="bg-cream border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TERM_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTermDialog(false)} className="text-xs">Cancel</Button>
            <Button onClick={saveTermToLibrary} className="bg-gold hover:bg-gold/90 text-white text-xs">Save to Library</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inclusions Library Dialog */}
      <Dialog open={showInclusionsLibrary} onOpenChange={(open) => { setShowInclusionsLibrary(open); if (!open) setEditingInclusion(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh]" data-testid="inclusions-library-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy">Inclusions Library</DialogTitle>
          </DialogHeader>
          <div className="py-2 overflow-y-auto max-h-[55vh]">
            <p className="text-xs text-muted-foreground mb-3">Click an inclusion to add it to the selected hotel. Use the pencil icon to edit.</p>
            {INCLUSION_CATEGORIES.map((cat) => {
              const items = inclusionsLibrary.filter((i) => i.category === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="mb-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gold mb-2">{cat}</h4>
                  <div className="space-y-1.5">
                    {items.map((inc) => (
                      editingInclusion?.id === inc.id ? (
                        <div key={inc.id} className="flex items-center gap-2 p-2 bg-white border border-gold">
                          <Input
                            value={editingInclusion.text}
                            onChange={(e) => setEditingInclusion((p) => ({ ...p, text: e.target.value }))}
                            className="bg-cream border-border text-sm h-8 flex-1"
                            autoFocus
                            onKeyDown={(e) => e.key === "Enter" && updateInclusionInLibrary()}
                          />
                          <Select value={editingInclusion.category} onValueChange={(v) => setEditingInclusion((p) => ({ ...p, category: v }))}>
                            <SelectTrigger className="bg-cream border-border h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {INCLUSION_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                            </SelectContent>
                          </Select>
                          <button onClick={updateInclusionInLibrary} className="text-navy hover:text-gold shrink-0" title="Save"><FloppyDisk size={16} /></button>
                          <button onClick={() => setEditingInclusion(null)} className="text-muted-foreground hover:text-destructive shrink-0"><X size={14} /></button>
                        </div>
                      ) : (
                        <div key={inc.id} className="flex items-start gap-2 p-2 bg-cream hover:bg-cream-mid cursor-pointer border border-transparent hover:border-gold transition-colors" onClick={() => addInclusionFromLibrary(inc.text)}>
                          <span className="text-sm flex-1">{inc.text}</span>
                          <button onClick={(e) => { e.stopPropagation(); setEditingInclusion({ id: inc.id, text: inc.text, category: inc.category }); }} className="text-muted-foreground hover:text-navy shrink-0" title="Edit"><PencilSimple size={14} /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteInclusionFromLibrary(inc.id); }} className="text-muted-foreground hover:text-destructive shrink-0" title="Delete"><X size={14} /></button>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              );
            })}
            {inclusionsLibrary.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">No saved inclusions yet. Save inclusions to build your library.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInclusionsLibrary(false); setEditingInclusion(null); }} className="text-xs">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Inclusion Dialog */}
      <Dialog open={showSaveInclusionDialog} onOpenChange={setShowSaveInclusionDialog}>
        <DialogContent className="max-w-md" data-testid="save-inclusion-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg text-navy">Save Inclusion to Library</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Inclusion Text</Label>
              <Input value={newInclusionToSave.text} onChange={(e) => setNewInclusionToSave((p) => ({ ...p, text: e.target.value }))} className="bg-cream border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
              <Select value={newInclusionToSave.category} onValueChange={(v) => setNewInclusionToSave((p) => ({ ...p, category: v }))}>
                <SelectTrigger className="bg-cream border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INCLUSION_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveInclusionDialog(false)} className="text-xs">Cancel</Button>
            <Button onClick={saveInclusionToLibrary} className="bg-gold hover:bg-gold/90 text-white text-xs">Save to Library</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Reservation Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy">Convert to Reservation</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">Select which hotel bookings to include in the reservation:</p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {convertItems.map((item, idx) => (
              <label
                key={idx}
                className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                  convertSelected.includes(idx) ? "border-gold bg-gold/5" : "border-border bg-white hover:bg-cream/50"
                }`}
              >
                <Checkbox
                  checked={convertSelected.includes(idx)}
                  onCheckedChange={() => toggleConvertItem(idx)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy">{item.hotel_name || "Unnamed Hotel"}</p>
                  <p className="text-xs text-muted-foreground">{item.city} · {item.room_type} · {item.num_rooms} room{item.num_rooms > 1 ? "s" : ""}</p>
                  <p className="text-xs text-muted-foreground">{item.check_in} → {item.check_out} ({item.nights}N)</p>
                </div>
                <span className="text-sm font-serif text-gold whitespace-nowrap">{item.currency} {item.total?.toLocaleString()}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2 pt-3 border-t border-border">
            <button className="text-xs text-muted-foreground hover:text-navy" onClick={() => setConvertSelected(convertSelected.length === convertItems.length ? [] : convertItems.map((_, i) => i))}>
              {convertSelected.length === convertItems.length ? "Deselect All" : "Select All"}
            </button>
            <span className="text-xs text-muted-foreground">{convertSelected.length} of {convertItems.length} selected</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={executeConvert} disabled={converting || convertSelected.length === 0} className="bg-navy hover:bg-[#2a3a5e] text-white gap-1.5">
              <ArrowsClockwise size={16} /> {converting ? "Converting..." : "Create Reservation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const HotelImages = ({ images, hotelIndex, onUpdate, separatePage, onToggleSeparate }) => {
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const MAX_IMAGES = 9;

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    const newImages = [...images];

    for (const file of files) {
      if (newImages.length >= MAX_IMAGES) break;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post("/upload/hotel-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newImages.push({
          id: res.data.id,
          url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`,
          path: res.data.path,
          is_hero: newImages.length === 0,
        });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    onUpdate(newImages);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addUrl = () => {
    if (!urlInput.trim() || images.length >= MAX_IMAGES) return;
    onUpdate([
      ...images,
      { id: crypto.randomUUID(), url: urlInput.trim(), path: "", is_hero: images.length === 0 },
    ]);
    setUrlInput("");
  };

  const removeImage = (imgId) => {
    const updated = images.filter((i) => i.id !== imgId);
    if (updated.length && !updated.some((i) => i.is_hero)) {
      updated[0].is_hero = true;
    }
    onUpdate(updated);
  };

  const setHero = (imgId) => {
    onUpdate(images.map((i) => ({ ...i, is_hero: i.id === imgId })));
  };

  return (
    <div className="mb-4" data-testid={`hotel-images-${hotelIndex}`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs text-muted-foreground">Property Images (max {MAX_IMAGES})</Label>
        {images.length > 0 && (
          <label className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox
              checked={separatePage}
              onCheckedChange={onToggleSeparate}
              className="border-border data-[state=checked]:bg-navy data-[state=checked]:border-navy h-3.5 w-3.5"
            />
            <span className="text-[10px] text-muted-foreground">Gallery page in PDF</span>
          </label>
        )}
      </div>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {images.map((img) => (
            <div
              key={img.id}
              className={`relative group aspect-square border-2 ${img.is_hero ? "border-gold" : "border-transparent"} bg-white overflow-hidden`}
            >
              <img
                src={img.url}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = ""; e.target.alt = "Error"; }}
              />
              {img.is_hero && (
                <span className="absolute top-0.5 left-0.5 bg-gold text-white text-[7px] uppercase px-1 font-bold">Hero</span>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                {!img.is_hero && (
                  <button
                    onClick={() => setHero(img.id)}
                    className="bg-white/90 rounded-full p-1 hover:bg-gold hover:text-white"
                    title="Set as hero"
                  >
                    <Star size={12} weight="fill" />
                  </button>
                )}
                <button
                  onClick={() => removeImage(img.id)}
                  className="bg-white/90 rounded-full p-1 hover:bg-red-500 hover:text-white"
                  title="Remove"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {images.length < MAX_IMAGES && (
        <div className="flex gap-2 items-center">
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-xs h-7 gap-1 border-border text-muted-foreground"
            data-testid={`upload-image-btn-${hotelIndex}`}
          >
            {uploading ? <Spinner size={12} className="animate-spin" /> : <ImageSquare size={12} />}
            {uploading ? "Uploading..." : "Upload"}
          </Button>
          <div className="flex gap-1 flex-1">
            <Input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addUrl()}
              placeholder="Paste image URL"
              className="bg-white border-border h-7 text-xs flex-1"
              data-testid={`image-url-input-${hotelIndex}`}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={addUrl}
              disabled={!urlInput.trim()}
              className="text-xs h-7 px-2 border-border text-muted-foreground"
              data-testid={`add-url-btn-${hotelIndex}`}
            >
              <Link size={12} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Hotel Block Component
const HotelBlock = ({
  hotel,
  hotelIndex,
  proposal,
  totalHotels,
  hotelLibrary,
  onUpdate,
  onRemove,
  onGenerateConfirmation,
  onMoveUp,
  onMoveDown,
  onAddRoom,
  onRemoveRoom,
  onUpdateRoom,
  onSortRoomsByPrice,
  toINR,
  fmtINR,
  onSelectFromLibrary,
  onSaveToLibrary,
  onSearchHotels,
  onToggleInclusion,
  inclusionsLibrary,
  onOpenInclusionsLibrary,
  onAddCustomInclusion,
  onRemoveInclusion,
  onSaveInclusionToLibrary,
  canRemove,
}) => {
  const [customIncText, setCustomIncText] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="bg-cream border border-border mb-3" data-testid={`hotel-block-${hotelIndex}`}>
      <div className="flex items-center gap-3 p-5 pb-3 cursor-pointer" onClick={() => setCollapsed((c) => !c)}>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onMoveUp}
            disabled={hotelIndex === 0}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-navy disabled:opacity-30"
            data-testid={`move-hotel-up-${hotelIndex}`}
          >
            <CaretUp size={16} weight="bold" />
          </Button>
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] bg-navy text-gold px-3 py-1">
            Hotel {hotelIndex + 1}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onMoveDown}
            disabled={hotelIndex === totalHotels - 1}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-navy disabled:opacity-30"
            data-testid={`move-hotel-down-${hotelIndex}`}
          >
            <CaretDown size={16} weight="bold" />
          </Button>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl font-bold text-navy truncate" data-testid={`hotel-name-display-${hotelIndex}`}>
            {hotel.hotel_name || <span className="text-muted-foreground/40 italic font-normal">Untitled Hotel</span>}
          </h3>
          {hotel.city && <span className="text-xs text-muted-foreground">{hotel.city}</span>}
        </div>
        <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            onClick={onSearchHotels}
            className="text-xs h-8 gap-1 border-gold text-gold hover:bg-gold hover:text-white"
          >
            <Globe size={14} />
            Search Hotels
          </Button>
          {hotelLibrary.length > 0 && (
            <Select onValueChange={onSelectFromLibrary}>
              <SelectTrigger className="w-[160px] h-8 text-xs bg-white border-border">
                <Buildings size={14} className="mr-1" />
                <SelectValue placeholder="From library" />
              </SelectTrigger>
              <SelectContent>
                {hotelLibrary.map((h) => (
                  <SelectItem key={h.id} value={h.id}>{h.hotel_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onSaveToLibrary}
            className="text-xs h-8 text-muted-foreground hover:text-navy"
          >
            Save to library
          </Button>
          {canRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="text-xs h-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              data-testid={`remove-hotel-${hotelIndex}`}
            >
              Remove
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onGenerateConfirmation}
            className="text-xs h-8 text-gold hover:text-gold/80 hover:bg-gold/10"
            data-testid={`confirm-hotel-${hotelIndex}`}
          >
            Confirmation
          </Button>
        </div>
        <CaretDown size={16} weight="bold" className={`text-muted-foreground transition-transform shrink-0 ${collapsed ? "-rotate-90" : ""}`} />
      </div>

      {!collapsed && <div className="px-5 pb-5">

      {/* Hotel Images */}
      <HotelImages
        images={hotel.images || []}
        hotelIndex={hotelIndex}
        onUpdate={(imgs) => onUpdate("images", imgs)}
        separatePage={hotel.images_separate_page || false}
        onToggleSeparate={(v) => onUpdate("images_separate_page", v)}
      />

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Hotel Name</Label>
          <Input
            value={hotel.hotel_name}
            onChange={(e) => onUpdate("hotel_name", e.target.value)}
            placeholder="e.g. The Hotel Seiryu Kyoto"
            className="bg-white border-border"
            data-testid={`hotel-name-${hotelIndex}`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">City / Country</Label>
          <Input
            value={hotel.city}
            onChange={(e) => onUpdate("city", e.target.value)}
            placeholder="e.g. Kyoto, Japan"
            className="bg-white border-border"
            data-testid={`hotel-city-${hotelIndex}`}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Brand / Chain</Label>
          <Input
            value={hotel.brand || ""}
            onChange={(e) => onUpdate("brand", e.target.value)}
            placeholder="e.g. Marriott, ITC, Taj"
            className="bg-white border-border"
            data-testid={`hotel-brand-${hotelIndex}`}
            list={`brand-suggest-${hotelIndex}`}
          />
          <datalist id={`brand-suggest-${hotelIndex}`}>
            {(proposal._brands || []).map((b) => <option key={b} value={b} />)}
          </datalist>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Country</Label>
          <Input
            value={hotel.country || ""}
            onChange={(e) => onUpdate("country", e.target.value)}
            placeholder="e.g. Japan"
            className="bg-white border-border"
            data-testid={`hotel-country-${hotelIndex}`}
          />
        </div>
      </div>

      {/* Sync dates toggle */}
      {proposal.hotels.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <Checkbox
            checked={!!hotel.city_group}
            onCheckedChange={(checked) => {
              if (checked) {
                onUpdate("city_group", hotel.city || `Group ${hotelIndex + 1}`);
              } else {
                onUpdate("city_group", "");
              }
            }}
            className="h-3.5 w-3.5 border-border data-[state=checked]:bg-navy data-[state=checked]:border-navy"
          />
          <span className="text-xs text-muted-foreground">Sync dates with other hotels in</span>
          {hotel.city_group ? (
            <Input
              value={hotel.city_group}
              onChange={(e) => onUpdate("city_group", e.target.value)}
              className="bg-white border-border h-7 text-xs w-32 inline-block"
              list={`city-groups-${hotelIndex}`}
            />
          ) : (
            <span className="text-xs text-muted-foreground/50">same city</span>
          )}
          <datalist id={`city-groups-${hotelIndex}`}>
            {[...new Set(proposal.hotels.filter((h) => h.city_group && h.id !== hotel.id).map((h) => h.city_group))].map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </div>
      )}

      <div className="space-y-1 mb-3">
        <Label className="text-xs text-muted-foreground">Full Address</Label>
        <Input
          value={hotel.address}
          onChange={(e) => onUpdate("address", e.target.value)}
          placeholder="e.g. 2-204-2 Kiyomizu, Higashiyama-ku, Kyoto"
          className="bg-white border-border"
        />
      </div>

      <div className="space-y-1 mb-3">
        <Label className="text-xs text-muted-foreground">Hotel Description (shown on proposal)</Label>
        <Textarea
          value={hotel.description}
          onChange={(e) => onUpdate("description", e.target.value)}
          placeholder="Brief description — location, signature features, why recommended"
          className="bg-white border-border min-h-[70px]"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Amenities (comma-separated)</Label>
          <Input
            value={hotel.amenities}
            onChange={(e) => onUpdate("amenities", e.target.value)}
            placeholder="Concierge, Dining, Spa, Pool, Gym"
            className="bg-white border-border"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Currency</Label>
          <Select value={hotel.currency} onValueChange={(v) => onUpdate("currency", v)}>
            <SelectTrigger className="bg-white border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Per-Hotel Inclusions */}
      <div className="bg-white border border-border p-4 mb-4">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-3 block">
          Inclusions for this hotel
        </Label>

        {/* Standard inclusions */}
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Standard</p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {HOTEL_INCLUSIONS.map((inc) => (
            <div key={inc.id} className="flex items-center gap-2">
              <Checkbox
                id={`${hotel.id}-${inc.id}`}
                checked={(hotel.inclusions || []).includes(inc.label)}
                onCheckedChange={() => onToggleInclusion(inc.label)}
                className="border-border data-[state=checked]:bg-navy data-[state=checked]:border-navy"
              />
              <label htmlFor={`${hotel.id}-${inc.id}`} className="text-xs cursor-pointer">{inc.label}</label>
            </div>
          ))}
        </div>

        {/* Saved library inclusions (excluding duplicates of standard ones) */}
        {(() => {
          const standardSet = new Set(DEFAULT_HOTEL_INCLUSIONS);
          const libraryExtras = (inclusionsLibrary || []).filter((li) => !standardSet.has(li.text));
          if (libraryExtras.length === 0) return null;
          return (
            <>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 mt-2">From Your Library</p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {libraryExtras.map((li) => (
                  <div key={li.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`${hotel.id}-lib-${li.id}`}
                      checked={(hotel.inclusions || []).includes(li.text)}
                      onCheckedChange={() => onToggleInclusion(li.text)}
                      className="border-border data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                    />
                    <label htmlFor={`${hotel.id}-lib-${li.id}`} className="text-xs cursor-pointer">{li.text}</label>
                  </div>
                ))}
              </div>
            </>
          );
        })()}

        {/* Custom inclusions already added (not in standard or library) */}
        {(() => {
          const standardSet = new Set(DEFAULT_HOTEL_INCLUSIONS);
          const librarySet = new Set((inclusionsLibrary || []).map((li) => li.text));
          const customs = (hotel.inclusions || []).filter((inc) => !standardSet.has(inc) && !librarySet.has(inc));
          if (customs.length === 0) return null;
          return (
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Custom Inclusions</p>
              <div className="flex flex-wrap gap-1.5">
                {customs.map((inc) => (
                  <span key={inc} className="inline-flex items-center gap-1 bg-cream-mid text-xs px-2 py-1 border border-border">
                    {inc}
                    <button onClick={() => onRemoveInclusion(inc)} className="text-muted-foreground hover:text-destructive"><X size={12} /></button>
                    <button onClick={() => onSaveInclusionToLibrary(inc)} className="text-muted-foreground hover:text-gold" title="Save to library"><BookmarkSimple size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Add custom inclusion */}
        <div className="flex gap-2">
          <Input
            value={customIncText}
            onChange={(e) => setCustomIncText(e.target.value)}
            placeholder="Add custom inclusion..."
            className="bg-cream border-border text-xs h-8"
            onKeyDown={(e) => {
              if (e.key === "Enter" && customIncText.trim()) {
                onAddCustomInclusion(customIncText.trim());
                setCustomIncText("");
              }
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8 shrink-0"
            onClick={() => {
              if (customIncText.trim()) {
                onAddCustomInclusion(customIncText.trim());
                setCustomIncText("");
              }
            }}
          >
            <Plus size={14} /> Add
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8 border-gold text-gold hover:bg-gold hover:text-white shrink-0"
            onClick={onOpenInclusionsLibrary}
            data-testid={`inclusions-library-btn-${hotelIndex}`}
          >
            <ListPlus size={14} /> From Library
          </Button>
        </div>
      </div>

      {/* Per-Hotel Cancellation Policy */}
      <div className="space-y-1 mb-4">
        <Label className="text-xs text-muted-foreground">Cancellation Policy for {hotel.hotel_name || `Hotel ${hotelIndex + 1}`}</Label>
        <Textarea
          value={hotel.cancellation_policy || ""}
          onChange={(e) => onUpdate("cancellation_policy", e.target.value)}
          placeholder="e.g. Free cancellation until 14 days before check-in. 100% charge within 14 days."
          className="bg-white border-border min-h-[60px]"
          data-testid={`hotel-cancellation-${hotelIndex}`}
        />
      </div>

      {/* Rooms */}
      <div className="flex items-center gap-4 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Checkbox
            id={`static-rate-${hotel.id}`}
            checked={hotel.static_rate || false}
            onCheckedChange={(checked) => onUpdate("static_rate", checked)}
            className="border-border data-[state=checked]:bg-gold data-[state=checked]:border-gold"
          />
          <label htmlFor={`static-rate-${hotel.id}`} className="text-xs text-muted-foreground cursor-pointer">
            Static rate (no dates)
          </label>
        </div>
        {!hotel.static_rate && (
          <div className="flex items-center gap-2 border-l border-border pl-4" data-testid={`dates-toggle-${hotelIndex}`}>
            <button
              onClick={() => onUpdate("dates_per_room", false)}
              className={`text-xs px-3 py-1.5 border transition-colors ${
                !hotel.dates_per_room
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-muted-foreground border-border hover:border-navy"
              }`}
              data-testid={`dates-per-hotel-btn-${hotelIndex}`}
            >
              Dates per hotel
            </button>
            <button
              onClick={() => onUpdate("dates_per_room", true)}
              className={`text-xs px-3 py-1.5 border transition-colors ${
                hotel.dates_per_room
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-muted-foreground border-border hover:border-navy"
              }`}
              data-testid={`dates-per-room-btn-${hotelIndex}`}
            >
              Dates per room
            </button>
          </div>
        )}
      </div>

      {/* Hotel-level dates — shown when "Dates per hotel" and not static */}
      {!hotel.static_rate && !hotel.dates_per_room && (
        <div className="grid grid-cols-4 gap-3 mb-3 bg-white border border-border p-3" data-testid={`hotel-dates-${hotelIndex}`}>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Check-in (all rooms)</Label>
            <Input
              type="date"
              value={hotel.check_in || ""}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => onUpdate("check_in", e.target.value)}
              className="bg-cream border-border"
              data-testid={`hotel-checkin-${hotelIndex}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Check-out (all rooms)</Label>
            <Input
              type="date"
              value={hotel.check_out || ""}
              min={hotel.check_in || new Date().toISOString().split("T")[0]}
              onChange={(e) => onUpdate("check_out", e.target.value)}
              className="bg-cream border-border"
              data-testid={`hotel-checkout-${hotelIndex}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground flex items-center gap-2">
              Nights
              {hotel.nights > 0 && (
                <span className="bg-navy text-white text-[10px] font-semibold px-2 py-0.5">{hotel.nights}N</span>
              )}
            </Label>
            <Input
              type="number"
              min="1"
              value={hotel.nights || ""}
              onChange={(e) => onUpdate("nights", parseInt(e.target.value) || 0)}
              placeholder="auto"
              className="bg-cream border-border"
            />
          </div>
          <div className="flex items-end pb-1">
            <p className="text-[10px] text-muted-foreground">Same dates apply to all rooms in this hotel</p>
          </div>
        </div>
      )}

      {hotel.rooms.map((room, roomIndex) => (
        <RoomBlock
          key={room.id}
          room={room}
          roomIndex={roomIndex}
          currency={hotel.currency}
          staticRate={hotel.static_rate || false}
          datesPerRoom={hotel.dates_per_room || false}
          toINR={(amt) => toINR(amt, hotel.currency)}
          fmtINR={fmtINR}
          onUpdate={(field, value) => onUpdateRoom(room.id, field, value)}
          onRemove={() => onRemoveRoom(room.id)}
          canRemove={hotel.rooms.length > 1}
        />
      ))}

      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={onAddRoom}
          className="border border-dashed border-border hover:border-navy text-muted-foreground hover:text-navy px-3 py-1.5 text-xs"
          data-testid={`add-room-${hotelIndex}`}
        >
          + Add room / villa category
        </button>
        {hotel.rooms.length > 1 && (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Sort by price</span>
            <button
              onClick={() => onSortRoomsByPrice("asc")}
              className="text-[10px] px-2 py-1 border border-border hover:border-navy text-muted-foreground hover:text-navy transition-colors"
              data-testid={`sort-rooms-asc-${hotelIndex}`}
            >
              Low → High
            </button>
            <button
              onClick={() => onSortRoomsByPrice("desc")}
              className="text-[10px] px-2 py-1 border border-border hover:border-navy text-muted-foreground hover:text-navy transition-colors"
              data-testid={`sort-rooms-desc-${hotelIndex}`}
            >
              High → Low
            </button>
          </div>
        )}
      </div>
      </div>}
    </div>
  );
};

// Room Block Component
const RoomBlock = ({ room, roomIndex, currency, staticRate, datesPerRoom, toINR, fmtINR, onUpdate, onRemove, canRemove }) => {
  const perRoomTotal = room.total || 0;
  const grandTotal = perRoomTotal * (room.num_rooms || 1);

  return (
    <div className="bg-white border border-border p-4 mt-3" data-testid={`room-block-${roomIndex}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gold">
          Room / Villa {roomIndex + 1}
        </span>
        {canRemove && (
          <button
            onClick={onRemove}
            className="ml-auto text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
          >
            <X size={14} /> remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Room / Villa Type</Label>
          <Input
            value={room.room_type}
            onChange={(e) => onUpdate("room_type", e.target.value)}
            placeholder="e.g. Deluxe King"
            className="bg-cream border-border"
            data-testid={`room-type-${roomIndex}`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Adults</Label>
          <Input
            type="number"
            min="1"
            value={room.adults}
            onChange={(e) => onUpdate("adults", parseInt(e.target.value) || 1)}
            className="bg-cream border-border"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Children</Label>
          <Input
            type="number"
            min="0"
            value={room.children}
            onChange={(e) => onUpdate("children", parseInt(e.target.value) || 0)}
            className="bg-cream border-border"
          />
        </div>
      </div>

      {/* Dates — shown per room only if dates_per_room is true, hidden if static rate */}
      {!staticRate && datesPerRoom && (
        <div className="grid grid-cols-4 gap-3 mb-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Check-in</Label>
            <Input
              type="date"
              value={room.check_in}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => onUpdate("check_in", e.target.value)}
              className="bg-cream border-border"
              data-testid={`room-checkin-${roomIndex}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Check-out</Label>
            <Input
              type="date"
              value={room.check_out}
              min={room.check_in || new Date().toISOString().split("T")[0]}
              onChange={(e) => onUpdate("check_out", e.target.value)}
              className="bg-cream border-border"
              data-testid={`room-checkout-${roomIndex}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground flex items-center gap-2">
              Nights
              {room.nights > 0 && (
                <span className="bg-navy text-white text-[10px] font-semibold px-2 py-0.5">{room.nights}N</span>
              )}
            </Label>
            <Input
              type="number"
              min="1"
              value={room.nights || ""}
              onChange={(e) => onUpdate("nights", parseInt(e.target.value) || 0)}
              placeholder="auto"
              className="bg-cream border-border"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">No. of Rooms</Label>
            <Input
              type="number"
              min="1"
              value={room.num_rooms}
              onChange={(e) => onUpdate("num_rooms", parseInt(e.target.value) || 1)}
              className="bg-cream border-border"
            />
          </div>
        </div>
      )}

      {/* Number of rooms when static rate */}
      {staticRate && (
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">No. of Rooms</Label>
            <Input
              type="number"
              min="1"
              value={room.num_rooms}
              onChange={(e) => onUpdate("num_rooms", parseInt(e.target.value) || 1)}
              className="bg-cream border-border"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Room Charge (excl. tax)</Label>
          <Input
            type="number"
            step="0.01"
            value={room.room_charge || ""}
            onChange={(e) => onUpdate("room_charge", parseFloat(e.target.value) || 0)}
            placeholder="2,082.79"
            className="bg-cream border-border"
            data-testid={`room-charge-${roomIndex}`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Service Charge</Label>
          <Input
            type="number"
            step="0.01"
            value={room.service_charge || ""}
            onChange={(e) => onUpdate("service_charge", parseFloat(e.target.value) || 0)}
            placeholder="343.66"
            className="bg-cream border-border"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Tax (VAT / GST)</Label>
          <Input
            type="number"
            step="0.01"
            value={room.tax || ""}
            onChange={(e) => onUpdate("tax", parseFloat(e.target.value) || 0)}
            placeholder="208.28"
            className="bg-cream border-border"
          />
        </div>
      </div>

      <div className="space-y-1 mb-3">
        <Label className="text-xs text-muted-foreground">Meal Plan & Inclusions</Label>
        <Input
          value={room.meal_plan}
          onChange={(e) => onUpdate("meal_plan", e.target.value)}
          placeholder="e.g. Bed & Breakfast — daily breakfast for 2"
          className="bg-cream border-border"
        />
      </div>

      {perRoomTotal > 0 && (
        <div className="bg-cream border border-border p-3 mt-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Proposed total per room (incl. taxes)</span>
            <div className="text-right">
              <span className="font-serif text-lg font-semibold text-gold">
                {currency} {perRoomTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {toINR && toINR(perRoomTotal) && (
                  <p className="text-[10px] text-muted-foreground">
                    Approx. {fmtINR(toINR(perRoomTotal))}
                  </p>
              )}
            </div>
          </div>
          {(room.num_rooms || 1) > 1 && (
            <div className="flex justify-between items-center border-t border-dashed border-border pt-2">
              <span className="text-xs text-muted-foreground font-semibold">Total for {room.num_rooms} rooms</span>
              <div className="text-right">
                <span className="font-serif text-base font-semibold text-navy">
                  {currency} {grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {toINR && toINR(grandTotal) && (
                  <p className="text-[10px] text-muted-foreground">
                    Approx. {fmtINR(toINR(grandTotal))}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewProposalPage;
